// Agp.tsx
import { useCallback, useEffect, useState } from 'react';
import { Agp } from '../api/agp';
import { fetchItems, fetchItemsByIds } from '../api/generic';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { Recipe } from '../models/Recipe';
import { prefixCollection } from '../utils/prefix';
import AgpOverview from './AgpOverview';

const AgpPage = () => {
  const { tenant } = useAuthContext();

  const [agp, setAgp] = useState<Agp[]>([]);
  const [batchMap, setBatchMap] = useState<Record<string, Batch>>({});
  const [recipeMap, setRecipeMap] = useState<Record<string, Recipe>>({});

  // Single source of truth: initial load AND refresh use this
  const loadAll = useCallback(async () => {
    // 1) AGP items
    const agps = await fetchItems<Agp>(prefixCollection('agp', tenant));

    // 2) Batches for the AGP batchIds
    const batchIds = [...new Set(agps.map((a) => a.batchId).filter(Boolean))];
    const batches = batchIds.length ? await fetchItemsByIds<Batch>(prefixCollection('batches', tenant), batchIds) : [];
    const bMap: Record<string, Batch> = {};
    for (const b of batches) bMap[b.id] = b;

    // 3) Recipes for those batches
    const recipeIds = [...new Set(batches.map((b) => b.recipeId).filter(Boolean))];
    const recipes = recipeIds.length
      ? await fetchItemsByIds<Recipe>(prefixCollection('recipes', tenant), recipeIds)
      : [];
    const rMap: Record<string, Recipe> = {};
    for (const r of recipes) rMap[r.id] = r;

    // Update local state so parent stays in sync with child refreshes
    setAgp(agps);
    setBatchMap(bMap);
    setRecipeMap(rMap);

    // Return data so AgpOverview can update its own local copies too
    return { agp: agps, batchMap: bMap, recipeMap: rMap };
  }, [tenant]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  return <AgpOverview agp={agp} batchMap={batchMap} recipeMap={recipeMap} refetchAll={loadAll} />;
};

export default AgpPage;
